import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveCallParticipant } from '../../lib/callParticipant';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  Minimize2,
  Maximize2,
  User,
  Pause,
  Play,
  Volume2
} from 'lucide-react';

// Diagnostics OFF in production. Enable per-session: localStorage.setItem('webrtc_debug','1') then reload.
const WEBRTC_DEBUG = (() => { try { return window.localStorage.getItem('webrtc_debug') === '1'; } catch (e) { return false; } })();
const dbg = (...a) => { if (WEBRTC_DEBUG) console.log(...a); };

const ActiveCallInterface = ({
  currentCall,
  callStatus,
  isOnHold,
  localVideoRef,
  remoteVideoRef,
  remoteStream,
  resolveStaff,
  onEndCall,
  onToggleMute,
  onToggleVideo,
  onToggleHold,
  onScreenShare,
  diag,
  iceInfo,
  wsReconnects
}) => {
  const { t } = useLanguage();
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [volume, setVolume] = useState(1);          // local-only, remote element volume (0..1)
  const [isSharing, setIsSharing] = useState(false); // best-effort: set when share starts
  const [shareMsg, setShareMsg] = useState('');      // honest error/limitation surface
  const remoteAudioRef = useRef(null);               // DEDICATED audible sink (works for voice + video)
  const [audioBlocked, setAudioBlocked] = useState(false); // browser autoplay-with-sound blocked → show "Enable sound"
  const [audioDiag, setAudioDiag] = useState({});    // TEMP diagnostic: remote audio element playback state
  const [showDiag, setShowDiag] = useState(WEBRTC_DEBUG);    // overlay toggle (debug only)

  const isVideoCall = currentCall?.type === 'video';
  const held = !!isOnHold;
  // Resolve the display identity. Staff↔staff calls resolve by firebase_uid from the roster; an
  // AI-Voice → human HANDOFF (role==='answerer') has NO staff participant (targetUser is null) — its
  // name comes from the accepted INTERACTION (currentCall.customerName), NEVER from the raw signaling
  // model. This is the fix for the "User null" render. See ../../lib/callParticipant (pure + tested).
  const { name: peerName, role: peerRole } = resolveCallParticipant({ currentCall, resolveStaff, t });

  // Bind the remote stream to a DEDICATED <audio> sink (always rendered, never display:none) so
  // BOTH voice and video calls are audible. The <video> element is kept MUTED and used only for
  // picture — a display:none/hidden <video> does not reliably play audio, and autoplay-with-sound
  // is commonly blocked, which is why real calls had no sound. We also call play() explicitly and,
  // if the browser blocks autoplay, surface an "Enable sound" button (a real user gesture unblocks).
  useEffect(() => {
    if (!remoteStream) return;
    const a = remoteAudioRef.current;
    const v = remoteVideoRef?.current;
    if (v) { v.srcObject = remoteStream; v.muted = true; v.play?.().catch(() => {}); }
    if (a) {
      a.srcObject = remoteStream;
      a.muted = held;          // honor hold (local mute of the other party)
      a.volume = volume;
      const aTracks = remoteStream.getAudioTracks();
      const snap = (playResult, errName) => ({
        exists: true,
        srcObject: !!a.srcObject,
        muted: a.muted,
        volume: a.volume,
        paused: a.paused,
        audioTracks: aTracks.length,
        trackEnabled: aTracks[0]?.enabled,
        trackMuted: aTracks[0]?.muted,
        trackState: aTracks[0]?.readyState,
        play: playResult,
        err: errName || '',
      });
      a.play()
        .then(() => { setAudioBlocked(false); const d = snap('resolved'); setAudioDiag(d); dbg('[WEBRTC-DIAG] audio.play resolved', JSON.stringify(d)); })
        .catch((err) => { setAudioBlocked(true); const d = snap('rejected', (err?.name || '') + ': ' + (err?.message || '')); setAudioDiag(d); dbg('[WEBRTC-DIAG] audio.play rejected', JSON.stringify(d)); });
    }
  }, [remoteStream, remoteVideoRef, held, volume]);

  const enableSound = () => {
    const a = remoteAudioRef.current;
    if (a) a.play().then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true));
  };

  // Call duration timer
  useEffect(() => {
    let interval;
    if (callStatus === 'connected') {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStatus]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleToggleMute = () => {
    const muted = onToggleMute();
    setIsMuted(muted);
  };

  const handleToggleVideo = () => {
    const videoOff = onToggleVideo();
    setIsVideoOff(videoOff);
  };

  const handleToggleHold = () => {
    if (typeof onToggleHold === 'function') onToggleHold();
  };

  // Volume controls the dedicated audio sink (the video element stays muted).
  useEffect(() => {
    if (remoteAudioRef.current) remoteAudioRef.current.volume = volume;
  }, [volume]);

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (remoteAudioRef.current) remoteAudioRef.current.volume = v;
  };

  const handleScreenShare = async () => {
    // Screen share is CAMERA-INDEPENDENT (getDisplayMedia): it now works on a voice call too — the hook
    // adds a video sender and renegotiates in place (no camera required, does not end the audio call).
    if (typeof onScreenShare !== 'function') return;
    try {
      setShareMsg('');
      await onScreenShare();           // resolves only if the browser grants capture
      setIsSharing(true);
    } catch (err) {
      setIsSharing(false);
      // Cancelling the browser picker is benign — show its friendly message, never a camera error.
      setShareMsg(err?.userMessage || err?.message || t('st.call.shareCouldNotStart'));
    }
  };

  if (!currentCall) return null;

  return (
    <div className={`fixed ${isMinimized ? 'bottom-4 right-4 w-80' : 'inset-0'} z-50`}>
      {!isMinimized && <div className="absolute inset-0 bg-black bg-opacity-80" />}
      
      <div className={`${
        isMinimized 
          ? 'relative bg-white rounded-lg shadow-2xl border' 
          : 'absolute inset-4 bg-gray-900 rounded-lg overflow-hidden'
      }`}>
        
        {/* Call Header */}
        <div className={`${
          isMinimized 
            ? 'p-4 border-b' 
            : 'absolute top-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4 z-10'
        } flex items-center justify-between`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold ${isMinimized ? 'text-gray-900' : 'text-white'}`}>
                {peerName}{peerRole ? ` — ${peerRole}` : ''}
              </h3>
              <div className="flex items-center space-x-2">
                <Badge className={`text-xs ${
                  held
                    ? 'bg-orange-100 text-orange-800'
                    : callStatus === 'connected'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {held
                    ? t('st.call.onHold', { dur: formatDuration(callDuration) })
                    : callStatus === 'connected'
                      ? t('st.call.connected', { dur: formatDuration(callDuration) })
                      : t('st.call.connecting')
                  }
                </Badge>
                {isVideoCall && (
                  <Badge className="bg-purple-100 text-purple-800 text-xs">
                    <Video className="h-3 w-3 mr-1" />
                    {t('st.call.video')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(!isMinimized)}
            className={isMinimized ? 'text-gray-600' : 'text-white hover:bg-white/20'}
          >
            {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </Button>
        </div>

        {/* DEDICATED remote audio sink — ALWAYS rendered, never display:none, carries the call audio
            for BOTH voice and video. The <video> below is MUTED (picture only). */}
        <audio ref={remoteAudioRef} autoPlay playsInline />

        {/* Remote video (picture only; muted — audio comes from the <audio> sink above). */}
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          muted
          className={(!isMinimized && isVideoCall) ? 'absolute inset-0 w-full h-full object-cover pt-20 pb-24 bg-black' : 'hidden'}
        />

        {/* Autoplay blocked → real user gesture to start audio (no silent failure). */}
        {audioBlocked && (
          <button
            onClick={enableSound}
            className="absolute top-20 left-1/2 -translate-x-1/2 z-20 bg-yellow-500 hover:bg-yellow-600 text-black text-sm font-semibold px-4 py-2 rounded-full shadow-lg"
          >
            {t('st.call.tapEnableCallAudio')}
          </button>
        )}

        {/* ---- WEBRTC DIAGNOSTIC OVERLAY — debug only (localStorage webrtc_debug=1) ---- */}
        {WEBRTC_DEBUG && showDiag && (
          <div className="fixed top-2 left-2 z-[60] w-[360px] max-h-[85vh] overflow-auto bg-black/85 text-green-300 text-[10px] font-mono p-2 rounded leading-tight">
            <div className="flex justify-between text-white font-bold">
              <span>WEBRTC-DIAG</span>
              <button onClick={() => setShowDiag(false)} className="text-red-300 px-1">✕</button>
            </div>
            <div>status={callStatus} hold={String(held)} video={String(isVideoCall)}</div>
            <div className={iceInfo?.turn > 0 ? 'text-green-300' : 'text-red-400'}>
              TURN: {iceInfo?.turn > 0 ? iceInfo.turn + ' present' : 'missing'} | STUN:{iceInfo?.stun || 0} ({iceInfo?.source || '-'})
            </div>
            <div>WS closes/reconnects: {wsReconnects ?? 0}</div>
            {diag ? (
              <>
                <div>conn={diag.connectionState} ice={diag.iceConnectionState}</div>
                <div>gather={diag.iceGatheringState} sig={diag.signalingState}</div>
                <div className={diag.pair ? '' : 'text-red-400'}>pair={diag.pair ? (diag.pair.local + '→' + diag.pair.remote) : '(none)'} rtt={diag.pair?.rtt ?? '-'}</div>
                <div className={(diag.audioIn?.packets > 0) ? 'text-green-300' : 'text-red-400'}>audIN pkts={diag.audioIn?.packets ?? 0} bytes={diag.audioIn?.bytes ?? 0}</div>
                <div>audOUT pkts={diag.audioOut?.packets ?? 0} bytes={diag.audioOut?.bytes ?? 0}</div>
                <div>vidIN pkts={diag.videoIn?.packets ?? 0} bytes={diag.videoIn?.bytes ?? 0} fdec={diag.videoIn?.framesDecoded ?? 0}</div>
                <div>vidOUT pkts={diag.videoOut?.packets ?? 0} bytes={diag.videoOut?.bytes ?? 0}</div>
                <div>remote={JSON.stringify(diag.remoteTracks)}</div>
                <div>local={JSON.stringify(diag.localTracks)}</div>
              </>
            ) : <div>(waiting for connection…)</div>}
            <div className={audioDiag.play === 'rejected' ? 'text-red-400' : ''}>
              audio.play={audioDiag.play || '-'} muted={String(audioDiag.muted)} paused={String(audioDiag.paused)} vol={audioDiag.volume ?? '-'}
            </div>
            <div>aTracks={audioDiag.audioTracks ?? '-'} en={String(audioDiag.trackEnabled)} tmuted={String(audioDiag.trackMuted)} st={audioDiag.trackState || '-'}</div>
            {audioDiag.err ? <div className="text-red-400">err={audioDiag.err}</div> : null}
          </div>
        )}
        {WEBRTC_DEBUG && !showDiag && (
          <button onClick={() => setShowDiag(true)} className="fixed top-2 left-2 z-[60] bg-black/70 text-green-300 text-[10px] font-mono px-2 py-1 rounded">DIAG</button>
        )}

        {/* Local Video (Picture-in-Picture) — video calls only */}
        {!isMinimized && isVideoCall && (
          <div className="absolute top-24 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-white shadow-lg z-10">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {isVideoOff && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <VideoOff className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>
        )}

        {/* Voice Call Interface */}
        {!isMinimized && !isVideoCall && (
          <div className="flex-1 flex items-center justify-center pt-20 pb-24">
            <div className="text-center text-white">
              <div className="w-32 h-32 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="h-16 w-16 text-white" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">{peerName}</h2>
              {peerRole && <p className="text-sm text-gray-400 mb-1">{peerRole}</p>}
              <p className="text-gray-300">
                {callStatus === 'connected'
                  ? t('st.call.callDuration', { dur: formatDuration(callDuration) })
                  : t('st.call.connecting')
                }
              </p>
            </div>
          </div>
        )}

        {/* Minimized Call Controls */}
        {isMinimized && (
          <div className="p-4">
            <div className="flex justify-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleMute}
                className={isMuted ? 'bg-red-50 border-red-200' : ''}
              >
                {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              
              {isVideoCall && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleVideo}
                  className={isVideoOff ? 'bg-red-50 border-red-200' : ''}
                >
                  {isVideoOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
                </Button>
              )}
              
              <Button
                onClick={onEndCall}
                className="bg-red-500 hover:bg-red-600 text-white"
                size="sm"
              >
                <PhoneOff className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Full Screen Call Controls */}
        {!isMinimized && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-6">
            <div className="flex justify-center space-x-4">
              <Button
                variant="outline"
                onClick={handleToggleMute}
                className={`w-14 h-14 rounded-full border-2 ${
                  isMuted 
                    ? 'bg-red-500 border-red-500 text-white hover:bg-red-600' 
                    : 'bg-white/20 border-white text-white hover:bg-white/30'
                }`}
              >
                {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
              </Button>

              <Button
                variant="outline"
                onClick={handleToggleHold}
                title={held ? t('st.call.resume') : t('st.call.hold')}
                className={`w-14 h-14 rounded-full border-2 ${
                  held
                    ? 'bg-orange-500 border-orange-500 text-white hover:bg-orange-600'
                    : 'bg-white/20 border-white text-white hover:bg-white/30'
                }`}
              >
                {held ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
              </Button>

              {isVideoCall && (
                <Button
                  variant="outline"
                  onClick={handleToggleVideo}
                  className={`w-14 h-14 rounded-full border-2 ${
                    isVideoOff
                      ? 'bg-red-500 border-red-500 text-white hover:bg-red-600'
                      : 'bg-white/20 border-white text-white hover:bg-white/30'
                  }`}
                >
                  {isVideoOff ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
                </Button>
              )}
              
              <Button
                variant="outline"
                onClick={handleScreenShare}
                disabled={!isVideoCall}
                title={isVideoCall ? (isSharing ? t('st.call.sharingScreen') : t('st.call.shareScreen')) : t('st.call.shareVideoOnly')}
                className={`w-14 h-14 rounded-full border-2 ${
                  !isVideoCall
                    ? 'bg-white/10 border-white/40 text-white/40 cursor-not-allowed'
                    : isSharing
                      ? 'bg-blue-500 border-blue-500 text-white hover:bg-blue-600'
                      : 'bg-white/20 border-white text-white hover:bg-white/30'
                }`}
              >
                <Monitor className="h-6 w-6" />
              </Button>

              <Button
                onClick={onEndCall}
                title={t('st.call.endCall')}
                className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white border-2 border-red-500"
              >
                <PhoneOff className="h-6 w-6" />
              </Button>
            </div>

            {/* Volume — local playback level of the other party (this device only) */}
            <div className="flex items-center justify-center gap-3 mt-4 text-white">
              <Volume2 className="h-5 w-5" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolume}
                title={t('st.call.callVolume')}
                aria-label={t('st.call.callVolume')}
                className="w-48 accent-blue-500"
              />
              <span className="text-xs w-10 text-right">{Math.round(volume * 100)}%</span>
            </div>

            {/* Honest screen-share state (no dead button, no fake success) */}
            {(!isVideoCall || shareMsg) && (
              <p className="text-center text-xs text-gray-300 mt-2">
                {shareMsg || t('st.call.shareVideoOnlyMsg')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveCallInterface;